import { createContext, memo, type ReactNode, useContext, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';
import { createStore, type StoreApi, useStore } from 'zustand';

import { reportErrorToBugsnag } from '@air/utils-error';

import { resolveWorkspaceAgentSelectAssetHighlightIndex } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/resolveWorkspaceAgentSelectAssetHighlightIndex';
import {
  type WorkspaceAgentSelectAssetBoard,
  type WorkspaceAgentSelectAssetResults,
  type WorkspaceAgentSelectAssetSourceKey,
  type WorkspaceAgentSelectAssetValue,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/workspaceAgentSelectAssetTypes';
import { type WorkspaceAgentMentionRowItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import {
  getFirstWorkspaceAgentMentionableIndex,
  getFirstWorkspaceAgentMentionHitIndex,
  isWorkspaceAgentMentionTreeItem,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';

export type WorkspaceAgentSelectAssetHighlightDirection = -1 | 1;

type WorkspaceAgentSelectAssetValueHandler = (value: WorkspaceAgentSelectAssetValue | null) => void;

const EMPTY_NODE_IDS: ReadonlySet<string> = new Set();

const noop = () => undefined;

const EMPTY_RESULTS: WorkspaceAgentSelectAssetResults = {
  hasNextPage: false,
  hasNextPageNodeIds: EMPTY_NODE_IDS,
  isError: false,
  isFetching: false,
  isLoading: false,
  isStale: false,
  items: [],
  onFetchNextPage: noop,
  onFetchNextPageForNode: noop,
  onRetry: noop,
};

const highlightIndexForQuery = (items: readonly WorkspaceAgentMentionRowItem[], query: string) =>
  query === '' ? getFirstWorkspaceAgentMentionableIndex(items) : getFirstWorkspaceAgentMentionHitIndex(items);

const clampedHighlightIndex = (index: number, itemCount: number) =>
  itemCount > 0 ? Math.min(index, itemCount - 1) : 0;

export type WorkspaceAgentSelectAssetStoreState = {
  assetHighlightedIndex: number;
  assetResults: WorkspaceAgentSelectAssetResults;
  boardHighlightedIndex: number;
  boardPickerQuery: string;
  boardResults: WorkspaceAgentSelectAssetResults;
  isAssetKeyboardHighlight: boolean;
  isBoardKeyboardHighlight: boolean;
  isBoardPickerOpen: boolean;
  isOpen: boolean;
  onValueChangeRef: { current: WorkspaceAgentSelectAssetValueHandler | undefined };
  query: string;
  selectedBoard: WorkspaceAgentSelectAssetBoard | null;
  source: WorkspaceAgentSelectAssetSourceKey;
  value: WorkspaceAgentSelectAssetValue | null;
  chooseSource: (source: WorkspaceAgentSelectAssetSourceKey) => void;
  clear: () => void;
  highlightAsset: (index: number) => void;
  highlightBoard: (index: number) => void;
  moveAssetHighlight: (direction: WorkspaceAgentSelectAssetHighlightDirection) => void;
  moveBoardHighlight: (direction: WorkspaceAgentSelectAssetHighlightDirection) => void;
  openBoardPicker: () => void;
  select: (item: WorkspaceAgentSelectAssetValue) => void;
  selectBoard: (board: WorkspaceAgentSelectAssetBoard) => void;
  selectItemById: (itemId: string) => void;
  setBoardPickerOpen: (isOpen: boolean) => void;
  setBoardPickerQuery: (query: string) => void;
  setOpen: (isOpen: boolean) => void;
  setQuery: (query: string) => void;
  setSource: (source: WorkspaceAgentSelectAssetSourceKey) => void;
};

export type WorkspaceAgentSelectAssetStoreInitialState = Partial<
  Pick<
    WorkspaceAgentSelectAssetStoreState,
    'boardPickerQuery' | 'isBoardPickerOpen' | 'isOpen' | 'query' | 'selectedBoard' | 'source' | 'value'
  >
>;

const createWorkspaceAgentSelectAssetStore = (initialState?: WorkspaceAgentSelectAssetStoreInitialState) =>
  createStore<WorkspaceAgentSelectAssetStoreState>()((set, get) => ({
    assetHighlightedIndex: 0,
    assetResults: EMPTY_RESULTS,
    boardHighlightedIndex: 0,
    boardPickerQuery: initialState?.boardPickerQuery ?? '',
    boardResults: EMPTY_RESULTS,
    isAssetKeyboardHighlight: false,
    isBoardKeyboardHighlight: false,
    isBoardPickerOpen: initialState?.isBoardPickerOpen ?? false,
    isOpen: initialState?.isOpen ?? false,
    onValueChangeRef: { current: undefined },
    query: initialState?.query ?? '',
    selectedBoard: initialState?.selectedBoard ?? null,
    source: initialState?.source ?? 'brand-kit',
    value: initialState?.value ?? null,
    chooseSource: (source) => {
      const assetHighlightedIndex = highlightIndexForQuery(get().assetResults.items, '');

      if (source === 'board') {
        set({ assetHighlightedIndex, isBoardPickerOpen: false, query: '', source: 'board' });
        return;
      }

      set({ assetHighlightedIndex, isBoardPickerOpen: false, query: '', source: 'brand-kit' });
    },
    clear: () => {
      const onValueChange = get().onValueChangeRef.current;

      set({ value: null });
      onValueChange?.(null);
    },
    highlightAsset: (index) => set({ assetHighlightedIndex: index, isAssetKeyboardHighlight: false }),
    highlightBoard: (index) => set({ boardHighlightedIndex: index, isBoardKeyboardHighlight: false }),
    moveAssetHighlight: (direction) =>
      set((state) => {
        const itemCount = state.assetResults.items.length;

        if (itemCount === 0) {
          return state;
        }

        const current = clampedHighlightIndex(state.assetHighlightedIndex, itemCount);

        return {
          assetHighlightedIndex: (current + direction + itemCount) % itemCount,
          isAssetKeyboardHighlight: true,
        };
      }),
    moveBoardHighlight: (direction) =>
      set((state) => {
        const itemCount = state.boardResults.items.length;

        if (itemCount === 0) {
          return state;
        }

        const current = clampedHighlightIndex(state.boardHighlightedIndex, itemCount);

        return {
          boardHighlightedIndex: (current + direction + itemCount) % itemCount,
          isBoardKeyboardHighlight: true,
        };
      }),
    openBoardPicker: () =>
      set((state) => ({
        assetHighlightedIndex: highlightIndexForQuery(state.assetResults.items, ''),
        boardPickerQuery: '',
        isBoardPickerOpen: true,
        query: '',
        source: 'board',
      })),
    select: (item) => {
      const onValueChange = get().onValueChangeRef.current;

      set({
        boardPickerQuery: '',
        isBoardPickerOpen: false,
        isOpen: false,
        query: '',
        value: item,
      });
      onValueChange?.(item);
    },
    selectBoard: (board) =>
      set({
        isBoardPickerOpen: false,
        query: '',
        selectedBoard: board,
        source: 'board',
      }),
    selectItemById: (itemId) => {
      const state = get();
      const item =
        state.assetResults.items.find((row) => row.id === itemId) ??
        state.boardResults.items.find((row) => row.id === itemId);

      if (!item) {
        return;
      }

      if (isWorkspaceAgentMentionTreeItem(item)) {
        if (state.boardResults.isStale) {
          return;
        }

        if (item.kind === 'library') {
          state.boardResults.onToggleExpanded?.(item.id, item.isExpanded);
          return;
        }

        state.selectBoard({ id: item.boardId, title: item.title });
        return;
      }

      if (state.assetResults.isStale) {
        return;
      }

      if (item.kind === 'asset' || item.kind === 'contextSourceFieldValue') {
        state.select(item);
      }
    },
    setBoardPickerOpen: (isOpen) =>
      set(isOpen ? { boardPickerQuery: '', isBoardPickerOpen: true } : { isBoardPickerOpen: false }),
    setBoardPickerQuery: (query) =>
      set((state) => ({
        boardHighlightedIndex: highlightIndexForQuery(state.boardResults.items, query),
        boardPickerQuery: query,
      })),
    setOpen: (isOpen) =>
      set(isOpen ? { isOpen: true } : { boardPickerQuery: '', isBoardPickerOpen: false, isOpen: false, query: '' }),
    setQuery: (query) =>
      set((state) => ({
        assetHighlightedIndex: highlightIndexForQuery(state.assetResults.items, query),
        query,
      })),
    setSource: (source) =>
      set((state) => ({
        assetHighlightedIndex: highlightIndexForQuery(state.assetResults.items, ''),
        query: '',
        source,
      })),
  }));

const WorkspaceAgentSelectAssetStoreContext = createContext<StoreApi<WorkspaceAgentSelectAssetStoreState> | null>(null);

export type WorkspaceAgentSelectAssetStoreProviderProps = {
  children: ReactNode;
  /** Read once, when this dropdown mounts. A later change does not reset the session. */
  initialState?: WorkspaceAgentSelectAssetStoreInitialState;
};

export const WorkspaceAgentSelectAssetStoreProvider = memo(
  ({ children, initialState }: WorkspaceAgentSelectAssetStoreProviderProps) => {
    const storeRef = useRef<StoreApi<WorkspaceAgentSelectAssetStoreState>>();

    // One store per dropdown. A module singleton would leak the last picker's open state into the next one.
    storeRef.current ??= createWorkspaceAgentSelectAssetStore(initialState);

    return (
      <WorkspaceAgentSelectAssetStoreContext.Provider value={storeRef.current}>
        {children}
      </WorkspaceAgentSelectAssetStoreContext.Provider>
    );
  },
);

WorkspaceAgentSelectAssetStoreProvider.displayName = 'WorkspaceAgentSelectAssetStoreProvider';

export const useWorkspaceAgentSelectAssetStoreApi = () => {
  const store = useContext(WorkspaceAgentSelectAssetStoreContext);

  if (!store) {
    const context = 'useWorkspaceAgentSelectAssetStore must be used within WorkspaceAgentSelectAssetStoreProvider';
    const error = new Error(context);

    reportErrorToBugsnag({
      context,
      error,
    });

    throw error;
  }

  return store;
};

export const useWorkspaceAgentSelectAssetStore = <T,>(selector: (state: WorkspaceAgentSelectAssetStoreState) => T): T =>
  useStore(useWorkspaceAgentSelectAssetStoreApi(), selector);

const publishWorkspaceAgentSelectAssetResults = (
  store: StoreApi<WorkspaceAgentSelectAssetStoreState>,
  assetResults: WorkspaceAgentSelectAssetResults,
  boardResults: WorkspaceAgentSelectAssetResults,
) => {
  const state = store.getState();
  const nextState: Partial<WorkspaceAgentSelectAssetStoreState> = {};

  if (state.assetResults !== assetResults) {
    nextState.assetResults = assetResults;

    if (state.assetResults.items !== assetResults.items) {
      const assetHighlightedIndex = resolveWorkspaceAgentSelectAssetHighlightIndex({
        nextItems: assetResults.items,
        previousIndex: state.assetHighlightedIndex,
        previousItems: state.assetResults.items,
        query: state.query,
      });

      if (assetHighlightedIndex !== state.assetHighlightedIndex) {
        nextState.assetHighlightedIndex = assetHighlightedIndex;
      }
    }
  }

  if (state.boardResults !== boardResults) {
    nextState.boardResults = boardResults;

    if (state.boardResults.items !== boardResults.items) {
      const boardHighlightedIndex = resolveWorkspaceAgentSelectAssetHighlightIndex({
        nextItems: boardResults.items,
        previousIndex: state.boardHighlightedIndex,
        previousItems: state.boardResults.items,
        query: state.boardPickerQuery,
      });

      if (boardHighlightedIndex !== state.boardHighlightedIndex) {
        nextState.boardHighlightedIndex = boardHighlightedIndex;
      }
    }
  }

  if (nextState.assetResults || nextState.boardResults) {
    store.setState(nextState);
  }
};

export const usePublishWorkspaceAgentSelectAssetResults = (
  assetResults: WorkspaceAgentSelectAssetResults,
  boardResults: WorkspaceAgentSelectAssetResults,
  onValueChange: WorkspaceAgentSelectAssetValueHandler | undefined,
) => {
  const store = useWorkspaceAgentSelectAssetStoreApi();

  store.getState().onValueChangeRef.current = onValueChange;

  useIsomorphicLayoutEffect(() => {
    publishWorkspaceAgentSelectAssetResults(store, assetResults, boardResults);
  }, [assetResults, boardResults, store]);
};
